import React, { useState, useEffect } from "react";
import Container from "@/components/marketing/Container";
import { base44 } from "@/api/base44Client";
import CobeGlobe from "@/components/marketing/CobeGlobe";
import { REGION_MARKERS } from "@/components/marketing/globeMarkers";
import Reveal from "@/components/marketing/Reveal";

export default function ReachVisualization() {
  const [count, setCount] = useState(null);
  const [outlets, setOutlets] = useState(null);

  useEffect(() => {
    let active = true;
    base44.entities.Package.filter(
      { category: "newswires", is_active: true },
      null,
      100
    )
      .then((data) => {
        if (!active) return;
        const list = data || [];
        setCount(list.length);
        setOutlets(list.reduce((s, p) => s + (p.media_outlets_count || 0), 0));
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, []);

  const outletsLabel =
    outlets != null ? `${(Math.floor(outlets / 100) * 100).toLocaleString()}+` : "—";

  const stats = [
    { value: count != null ? count : "—", label: "Newswire networks" },
    { value: outletsLabel, label: "Media outlets" },
    { value: "7", label: "Major regions" },
    { value: "Google News", label: "Indexed" },
  ];

  return (
    <section className="relative overflow-hidden bg-showcase">
      {/* Faint star texture */}
      <div
        className="absolute inset-0 pointer-events-none opacity-40"
        style={{
          backgroundImage:
            "radial-gradient(1.2px 1.2px at 15% 22%, rgba(255,255,255,0.7), transparent), radial-gradient(1px 1px at 35% 68%, rgba(255,255,255,0.5), transparent), radial-gradient(1.4px 1.4px at 62% 18%, rgba(255,255,255,0.6), transparent), radial-gradient(1px 1px at 78% 55%, rgba(255,255,255,0.45), transparent), radial-gradient(1.2px 1.2px at 88% 30%, rgba(255,255,255,0.55), transparent), radial-gradient(1px 1px at 50% 85%, rgba(255,255,255,0.4), transparent), radial-gradient(1px 1px at 25% 45%, rgba(255,255,255,0.35), transparent), radial-gradient(1.3px 1.3px at 70% 78%, rgba(255,255,255,0.45), transparent)",
          backgroundRepeat: "repeat",
          backgroundSize: "420px 420px",
        }}
      />

      <Container className="relative pt-20 pb-24 sm:pt-24 sm:pb-28">
        {/* Heading */}
        <Reveal>
          <div className="text-center mb-12">
            <span className="inline-block font-mono text-xs uppercase tracking-widest px-2 py-1 border-[0.5px] border-[#3a3352] text-[#8a86a8] mb-4">
              REACH
            </span>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight mb-4">
              One submission. Global reach.
            </h2>
            <p className="text-mist text-base lg:text-lg max-w-2xl mx-auto leading-relaxed">
              Your announcement radiates across newswire networks, reaching thousands of outlets in every major region, fast.
            </p>
          </div>
        </Reveal>

        <div className="relative max-w-4xl mx-auto">
          {/* Globe with atmospheric purple glow.
              CobeGlobe applies an internal -translate-y-[10%] that pulls the
              globe upward, creating asymmetric visual spacing (extra gap below,
              reduced gap above). The wrapper below applies a matching +10%
              translate so the net transform is zero: the globe sits in its
              natural layout position, making the gap above (heading mb-12) and
              the gap below (stats mt-12) pixel-equal at any screen size. */}
          <div className="relative flex items-center justify-center py-0">
            <div
              className="absolute inset-0 pointer-events-none"
              style={{
                background:
                  "radial-gradient(ellipse 80% 60% at 50% 50%, rgba(114,0,203,0.06) 0%, transparent 70%)",
              }}
            />
            <div className="translate-y-[10%]">
              <CobeGlobe
                markers={REGION_MARKERS}
                rotationSpeed={0.009}
                markerColor={[0.6, 0.15, 1.0]}
                glowColor={[0.4, 0.12, 0.6]}
                ariaLabel="Rotating globe showing newswire syndication spreading across major regions"
                maxWidth="400px"
              />
            </div>
          </div>

          {/* Stat labels */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-12 mb-24 sm:mb-28">
            {stats.map((stat) => (
              <div key={stat.label} className="text-center">
                <div className="font-heading text-2xl font-bold text-lilac">{stat.value}</div>
                <div className="text-xs text-faded mt-1 uppercase tracking-wider">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </Container>
    </section>
  );
}